import { useState, useEffect } from 'react';
import { api, endpoints } from '../config/api';
import { useAuth } from '../context/AuthContext';
import ImageUploadField from '../components/ImageUploadField';

const defaultLogo = () => ({ url: '', x: 10, y: 10, width: 60, height: 40 });
const defaultWelcomeLogo = () => ({ url: '', position: 'bottom-left' });

const SECTIONS = [
  { id: 'branding', label: '🏷️ Branding', icon: '🎨' },
  { id: 'colores', label: '🎨 Colores', icon: '🌈' },
  { id: 'contenido', label: '📝 Contenido', icon: '📄' },
  { id: 'multimedia', label: '🖼️ Multimedia', icon: '📷' },
  { id: 'logos', label: '🖼️ Logos', icon: '🏢' },
];

function DesignStudioPage() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [activeEventId, setActiveEventId] = useState('');
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('branding');

  useEffect(() => {
    api.get(endpoints.events(), token).then((e) => {
      setEvents(e);
      setActiveEventId(e[0]?.id || '');
    }).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!activeEventId) return;
    api.get(endpoints.configurations(activeEventId), token)
      .then((data) => {
        setConfig({
          ...data,
          additionalLogos: Array.isArray(data.additionalLogos) && data.additionalLogos.length > 0 ? data.additionalLogos : [],
          welcomeLogos: Array.isArray(data.welcomeLogos) && data.welcomeLogos.length > 0 ? data.welcomeLogos : [
            { url: '', position: 'bottom-left' },
            { url: '', position: 'bottom-right' },
          ],
        });
      })
      .catch(() => setConfig({ additionalLogos: [], welcomeLogos: [{ url: '', position: 'bottom-left' }, { url: '', position: 'bottom-right' }] }));
  }, [activeEventId, token]);

  const handleChange = (key, value) => setConfig((c) => ({ ...c, [key]: value }));

  const additionalLogos = config.additionalLogos || [];
  const welcomeLogos = config.welcomeLogos || [{ url: '', position: 'bottom-left' }, { url: '', position: 'bottom-right' }];

  const addLogo = () => setConfig((c) => ({ ...c, additionalLogos: [...(c.additionalLogos || []), defaultLogo()] }));
  const updateLogo = (index, field, value) => {
    setConfig((c) => {
      const logos = [...(c.additionalLogos || [])];
      logos[index] = { ...logos[index], [field]: ['x', 'y', 'width', 'height'].includes(field) ? Number(value) || 0 : value };
      return { ...c, additionalLogos: logos };
    });
  };
  const removeLogo = (index) => setConfig((c) => ({ ...c, additionalLogos: (c.additionalLogos || []).filter((_, i) => i !== index) }));

  const updateWelcomeLogo = (index, field, value) => {
    setConfig((c) => {
      const logos = [...(c.welcomeLogos || [])];
      if (!logos[index]) logos[index] = defaultWelcomeLogo();
      logos[index] = { ...logos[index], [field]: value };
      return { ...c, welcomeLogos: logos };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(endpoints.configurations(activeEventId), config, token);
      alert('✅ Configuración guardada correctamente');
    } catch (err) {
      alert(err?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[200px]"><p className="text-gray-500">Cargando...</p></div>;

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Panel de configuración con pestañas */}
      <div className="flex-1 max-w-xl">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Studio de Diseño</h1>
        <p className="text-gray-600 mb-4">Configuración gráfica e interactiva del landing.</p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Evento</label>
          <select
            value={activeEventId}
            onChange={(e) => setActiveEventId(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.nombre}</option>
            ))}
          </select>
        </div>

        {/* Tabs de secciones */}
        <div className="flex gap-1 mb-4 overflow-x-auto pb-2">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeSection === s.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Sección: Branding */}
          {activeSection === 'branding' && (
            <div className="p-6 space-y-4">
              <h3 className="font-semibold text-gray-800 border-b pb-2">Identidad visual</h3>
              <ImageUploadField label="Imagen de título (header)" value={config.headerImage} onChange={(v) => handleChange('headerImage', v)} token={token} />
              <ImageUploadField label="Logo principal" value={config.logoUrl} onChange={(v) => handleChange('logoUrl', v)} token={token} />
              <ConfigField label="Título pestaña del navegador" type="text" value={config.tabTitle} onChange={(v) => handleChange('tabTitle', v)} placeholder="Mi Evento" />
              <ConfigField label="Descripción meta" type="textarea" value={config.tabDescription} onChange={(v) => handleChange('tabDescription', v)} placeholder="Explora el evento..." />
            </div>
          )}

          {/* Sección: Colores */}
          {activeSection === 'colores' && (
            <div className="p-6 space-y-4">
              <h3 className="font-semibold text-gray-800 border-b pb-2">Paleta de colores</h3>
              <ConfigField label="Fuente (font-family)" type="text" value={config.fontFamily} onChange={(v) => handleChange('fontFamily', v)} placeholder="inherit, sans-serif, etc." />
              <ConfigField label="Color de texto principal" type="color" value={config.fontColor} onChange={(v) => handleChange('fontColor', v)} />
              <ConfigField label="Color de texto secundario" type="color" value={config.fontColorSecondary} onChange={(v) => handleChange('fontColorSecondary', v)} />
              <ConfigField label="Color primario" type="color" value={config.primaryColor} onChange={(v) => handleChange('primaryColor', v)} />
              <ConfigField label="Color secundario" type="color" value={config.secondaryColor} onChange={(v) => handleChange('secondaryColor', v)} />
              <ConfigField label="Color de fondo (fallback)" type="color" value={config.bodyBackground} onChange={(v) => handleChange('bodyBackground', v)} />
              <h4 className="font-medium text-gray-700 pt-2">Botones</h4>
              <ConfigField label="Color de botón" type="color" value={config.buttonColor} onChange={(v) => handleChange('buttonColor', v)} />
              <ConfigField label="Color texto botón" type="color" value={config.buttonTextColor} onChange={(v) => handleChange('buttonTextColor', v)} />
            </div>
          )}

          {/* Sección: Contenido */}
          {activeSection === 'contenido' && (
            <div className="p-6 space-y-4">
              <h3 className="font-semibold text-gray-800 border-b pb-2">Textos y enlaces</h3>
              <ConfigField label="Título principal" type="text" value={config.headerText} onChange={(v) => handleChange('headerText', v)} placeholder="Bienvenido" />
              <ConfigField label="Texto del botón" type="text" value={config.buttonText} onChange={(v) => handleChange('buttonText', v)} placeholder="Iniciar sesión" />
              <ConfigField label="Enlace de recursos" type="text" value={config.resourcesLink} onChange={(v) => handleChange('resourcesLink', v)} placeholder="https://..." />
            </div>
          )}

          {/* Sección: Multimedia */}
          {activeSection === 'multimedia' && (
            <div className="p-6 space-y-4">
              <h3 className="font-semibold text-gray-800 border-b pb-2">Imágenes y streaming</h3>
              <ImageUploadField label="Imagen de fondo" value={config.bodyBackgroundImage} onChange={(v) => handleChange('bodyBackgroundImage', v)} token={token} />
              <ImageUploadField label="Imagen mujer (header)" value={config.mujerKvUrl} onChange={(v) => handleChange('mujerKvUrl', v)} token={token} />
              <ConfigField label="StreamYard / Streaming (URL embed)" type="text" value={config.headerVideo} onChange={(v) => handleChange('headerVideo', v)} placeholder="https://streamyard.com/embed/..." />
            </div>
          )}

          {/* Sección: Logos */}
          {activeSection === 'logos' && (
            <div className="p-6 space-y-6">
              <h3 className="font-semibold text-gray-800 border-b pb-2">Logos adicionales (posicionamiento libre)</h3>
              <p className="text-sm text-gray-500">Logos flotantes con posición en % y tamaño en px.</p>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Logos</span>
                <button type="button" onClick={addLogo} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-medium">+ Añadir</button>
              </div>
              {additionalLogos.map((logo, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg border space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-sm">Logo {idx + 1}</span>
                    <button type="button" onClick={() => removeLogo(idx)} className="text-red-600 hover:text-red-800 text-sm">Eliminar</button>
                  </div>
                  <ImageUploadField label="URL" value={logo.url} onChange={(v) => updateLogo(idx, 'url', v)} token={token} />
                  <div className="grid grid-cols-4 gap-2">
                    {['x', 'y', 'width', 'height'].map((f) => (
                      <div key={f}>
                        <label className="block text-xs text-gray-500 mb-0.5">{f === 'x' ? 'X %' : f === 'y' ? 'Y %' : f === 'width' ? 'Ancho px' : 'Alto px'}</label>
                        <input type="number" min={f === 'x' || f === 'y' ? 0 : 10} max={f === 'x' || f === 'y' ? 100 : 500} value={logo[f] ?? (f === 'x' || f === 'y' ? 10 : 60)} onChange={(e) => updateLogo(idx, f, e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <h3 className="font-semibold text-gray-800 border-b pb-2 pt-4">Logos inferiores (welcome)</h3>
              <p className="text-sm text-gray-500">Logos en la parte inferior: izquierda y derecha.</p>
              {[0, 1].map((idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg border">
                  <ImageUploadField label={idx === 0 ? 'Logo izquierdo (Medicina Biorreguladora)' : 'Logo derecho (Heel)'} value={welcomeLogos[idx]?.url} onChange={(v) => updateWelcomeLogo(idx, 'url', v)} token={token} />
                </div>
              ))}
            </div>
          )}
        </div>

        <button onClick={handleSave} disabled={saving} className="w-full mt-6 py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-lg">
          {saving ? 'Guardando...' : '💾 Guardar configuración'}
        </button>
      </div>

      {/* Vista previa en vivo */}
      <div className="flex-1 min-w-0">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Vista previa en vivo</h2>
        <div
          className="rounded-xl border-2 border-gray-300 overflow-hidden shadow-2xl relative"
          style={{
            maxWidth: 420,
            minHeight: 600,
            background: config.bodyBackgroundImage ? `linear-gradient(180deg, rgba(15,60,50,0.9) 0%, rgba(20,80,70,0.85) 100%), url(${config.bodyBackgroundImage}) center/cover` : (config.bodyBackground || '#0f3c32'),
          }}
        >
          <div className="p-6 relative min-h-[600px] flex flex-col">
            {/* Header con título e imagen mujer */}
            <div className="flex items-end justify-between mb-6">
              {config.headerImage && <img src={config.headerImage} alt="" className="max-h-16 w-auto object-contain" />}
              {config.mujerKvUrl && <img src={config.mujerKvUrl} alt="" className="h-24 w-auto object-contain opacity-70" />}
            </div>
            <h1 className="text-white text-xl font-bold mb-2">{config.headerText || 'Bienvenido'}</h1>
            <p className="text-white/80 text-sm mb-6">{config.tabDescription || 'Explora el evento'}</p>

            {/* Botones de ejemplo */}
            <div className="space-y-3 flex-1">
              <div className="h-12 rounded-xl flex items-center justify-center font-semibold" style={{ backgroundColor: config.buttonColor || '#a3e635', color: config.buttonTextColor || '#1f2937' }}>
                {config.buttonText || 'Iniciar sesión'}
              </div>
              <div className="h-12 rounded-xl flex items-center justify-center font-semibold bg-white/20 text-white border border-white/30">
                Registrarme
              </div>
            </div>

            {/* Título de página */}
            <div className="my-4 py-2 px-4 rounded-full text-center font-semibold" style={{ backgroundColor: '#CEF630', color: '#0f3c32' }}>Speakers</div>

            {/* Logos inferiores */}
            <div className="flex justify-between items-end mt-auto pt-6">
              {welcomeLogos[0]?.url && <img src={welcomeLogos[0].url} alt="" className="h-8 object-contain" />}
              <div className="flex-1" />
              {welcomeLogos[1]?.url && <img src={welcomeLogos[1].url} alt="" className="h-8 object-contain" />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfigField({ label, type, value, onChange, placeholder }) {
  if (type === 'color') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="flex gap-2 items-center">
          <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} className="w-14 h-10 rounded-lg cursor-pointer border" />
          <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="#hex" className="flex-1 px-3 py-2 border rounded-lg" />
        </div>
      </div>
    );
  }
  if (type === 'imageUrl') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="flex gap-3">
          <input type="url" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="flex-1 px-3 py-2 border rounded-lg" />
          {value && <img src={value} alt="" className="w-16 h-16 object-cover rounded-lg border" onError={(e) => { e.target.style.display = 'none'; }} />}
        </div>
      </div>
    );
  }
  if (type === 'textarea') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 border rounded-lg" rows={2} />
      </div>
    );
  }
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 border rounded-lg" />
    </div>
  );
}

export default DesignStudioPage;
